import {Component, Inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {PushpinService} from "../../services/pushpin/pushpin.service";
import {CustomerArea} from "../../classes/customerarea.class";
import {PushpinFilter} from "../../pushpin-filter";
import {PricesService} from "../../services/prices/prices.service";
import {StatusService} from "../../services/status/status.service";
import {MaterialService} from "../../services/material/material.service";
import {DecorationsService} from "../../services/decorations/decorations.service";
import {PaintColorService} from "../../services/paintColor/paint-color.service";
import {PushpinPart} from "../../classes/pushpinpart.class";
import {combineLatest, forkJoin} from "rxjs";
import {formatDate} from "@angular/common";
import {Inspection} from '../../classes/inspection';
import * as shpwrite from '@mapbox/shp-write';
import {saveAs} from 'file-saver';
import {CodaltComponent} from 'src/app/codalt.component';
import {Status} from 'src/app/classes/status.class';
import {Settings} from 'src/app/settings.class';
import {ShapefileImportService} from '../../services/shapefile-import/shapefile-import.service';
import {ShapefileImport} from '../../classes/shapefile-import.class';
import {ConfirmDialogService} from '../../services/confirm-dialog-service/confirm-dialog.service';
import {createUpdatedShapefile} from './updated-shapefile';

@Component({
    selector: 'app-pushpin-shape-export',
    templateUrl: './pushpin-shape-export.component.html',
    styleUrls: ['./pushpin-shape-export.component.scss']
})
export class PushpinShapeExportComponent extends CodaltComponent implements OnInit {

    customerArea: CustomerArea;
    inspection: Inspection;
    public exporting = false;

    statuses: Status[];
    filter = new PushpinFilter();
    statusCounts = new Map<number, number>();
    // Shapefiles the parts of this inspection were imported from, these can be returned with the VVR data added
    shapefileImports: ShapefileImport[] = [];
    exportingShapefileImportId: number = null;

    constructor(private dialogRef: MatDialogRef<PushpinShapeExportComponent>,
                private pushpinService: PushpinService,
                private pricesService: PricesService,
                private statusService: StatusService,
                private materialService: MaterialService,
                private decorationsService: DecorationsService,
                private paintColorService: PaintColorService,
                private shapefileImportService: ShapefileImportService,
                private confirmDialogService: ConfirmDialogService,
                @Inject(MAT_DIALOG_DATA) data: {
                    customerArea: CustomerArea,
                    inspection: Inspection
                }) {
        super();
        this.inspection = data.inspection;
        this.customerArea = data.customerArea;
    }

    ngOnInit(): void {
        this.statusService.getStatuses(this.inspection?.inspection_type_id).then((statuses) => {
            statuses.forEach((status, i) => {
                status['img'] = Settings.getMarkerForStatus(i, status.color);
            });
            this.statuses = statuses;
            this.getStatusCounts();
        });
        if (this.inspection) {
            this.subscriptions.add(this.shapefileImportService.getImports(this.inspection.id).subscribe(shapefileImports => {
                this.shapefileImports = shapefileImports ?? [];
            }));
        }
    }

    /** Downloads the delivered shapefile with the status, CROW etc. of every imported part added */
    exportShapefileImport(shapefileImport: ShapefileImport) {
        this.exportingShapefileImportId = shapefileImport.id;
        forkJoin([
            this.shapefileImportService.getOriginal(shapefileImport.id),
            this.shapefileImportService.getParts(shapefileImport.id)
        ]).subscribe({
            next: async ([original, parts]) => {
                try {
                    const updated = await createUpdatedShapefile(await (original as Blob).arrayBuffer(), parts, shapefileImport.id, shapefileImport.reference_field);
                    const name = shapefileImport.filename.replace(/\.zip$/i, '');
                    saveAs(new Blob([updated], {type: 'application/zip'}), `${name}_VVR_${formatDate(new Date(), 'yyyy-MM-dd', 'nl')}.zip`);
                } catch (error) {
                    this.showShapefileExportError(error);
                }
                this.exportingShapefileImportId = null;
            },
            error: error => {
                this.showShapefileExportError(error);
                this.exportingShapefileImportId = null;
            }
        });
    }

    private showShapefileExportError(error: any) {
        console.error('Error exporting shapefile:', error);
        this.confirmDialogService.confirm('Fout bij exporteren', 'Het shapefile kon niet worden bijgewerkt. ' + (error?.message ?? ''), 'Sluiten', null)
            .catch(() => {
            });
    }

    getStatusCounts() {
        this.subscriptions.add(this.statusService.getStatusCounts(this.customerArea?.id, this.inspection?.id).subscribe(statusCounts => {
            this.statusCounts.clear();
            statusCounts.data.forEach(counts => {
                this.statusCounts.set(counts.id, counts.count);
            });
        }));
    }

    export() {
        this.exporting = true;

        const status$ = this.statusService.getKeyValMap();
        const decorations$ = this.decorationsService.getKeyValMap();
        const materials$ = this.materialService.getMaterialsAsKeyValueMap(true);
        const paintColor$ = this.paintColorService.getKeyValMap();
        combineLatest([status$, decorations$, materials$, paintColor$])
            .subscribe(([status, decoration, material, paintColor]) => {
                const keyValMap = {};
                keyValMap['status_id'] = status;
                keyValMap['decoration_id'] = decoration;
                keyValMap['material_id'] = material;
                keyValMap['paint_color_id'] = paintColor;
                this.makeShapeFile(keyValMap);
            });
    }

    onNoClick() {
        this.dialogRef.close();
    }

    private makeShapeFile(keyValMap: {}) {
        if (this.inspection) {
            this.filter.inspection_id = this.inspection.id;
        }
        this.pushpinService.getPushpins(this.customerArea.id, this.filter).then(pushpins => {
            this.subscriptions.add(this.pricesService.getItemsWithPrices([this.inspection.id]).subscribe(prices => {
                const materialPrices = prices['materialPrices'];
                const decorationPrices = prices['decorationPrices'];
                const pointFeatures = [];
                const lineFeatures = [];
                pushpins.forEach(pushpin => {
                    if (pushpin.pushpin_parts && pushpin.pushpin_parts.length > 0) {
                        pushpin.pushpin_parts.forEach(pushpinPart => {
                            this.getAllData(pushpinPart, keyValMap);
                            if (!pushpinPart.decoration) {
                                return;
                            }
                            if (pushpinPart.decoration?.type === 'Vlak' || pushpinPart.decoration?.type === 'Diversen vervangen' || !pushpinPart.decoration?.specify_length) {
                                pushpinPart.surface = this.pricesService.calculateSurface(pushpinPart);
                            }
                            const price = this.pricesService.calculatePushpinPartPriceDetail(pushpinPart, materialPrices, decorationPrices);

                            const properties = {
                                number: `${pushpin.number}`,
                                p_id: pushpin.id,
                                pp_id: pushpinPart.id,
                                upd_at: pushpinPart.updated_at,
                                exec_date: pushpinPart.executedDate,
                                zipcode: pushpin.zipcode,
                                place: pushpin.place,
                                street: pushpin.street,
                                housenr: pushpin.housenumber,
                                total: price.total,
                                material: price.material,
                                matCode: price.materialCode,
                                primer: price.primer,
                                primCode: price.primerCode,
                                blast: price.blast,
                                blastCode: price.blastCode,
                                stake_out: price.stake_out,
                                stakeCode: price.stake_outCode,
                                exec_by: pushpinPart.executedBy,
                                creat_by: pushpinPart.created_by,
                                in_quot: pushpinPart.inQuotation,
                                status: pushpinPart.status?.name,
                                decor: pushpinPart.decoration?.name,
                                mat_name: pushpinPart.material?.name,
                                prim_pt: pushpinPart.primer,
                                paint_col: pushpinPart.paint_color?.name,
                                length: pushpinPart.length,
                                surface: pushpinPart.decoration?.specify_length ? pushpinPart.length * pushpinPart.decoration.surface : pushpinPart.surface,
                                amount: pushpinPart.amount,
                                crow: pushpinPart.crow,
                                blast_pt: pushpinPart.blast,
                                stake_pt: pushpinPart.stake_out,
                                prior_fig: pushpinPart.decoration?.priority_figuration,
                                turbo_brn: pushpinPart.turbo_burn,
                                man_brn: pushpinPart.manual_burn,
                                night: pushpinPart.night,
                                desc: pushpinPart.description,
                                ref: pushpin.reference,
                                cust_note: pushpinPart.customer_note
                            };

                            if (pushpin.lengthmark) {
                                let snappedData;
                                try {
                                    snappedData = JSON.parse(pushpin.snappedpoints);
                                } catch {
                                    snappedData = pushpin.snappedpoints;
                                }
                                if (snappedData && snappedData.snappedPoints && snappedData.snappedPoints.length > 1) {
                                    const points = snappedData.snappedPoints.map((p: any) => [parseFloat(p.lng ?? p.longitude), parseFloat(p.lat ?? p.latitude)]);
                                    const geometry = {
                                        type: 'LineString',
                                        coordinates: points
                                    };
                                    const feature = {
                                        type: 'Feature',
                                        geometry: geometry,
                                        properties: properties
                                    };
                                    lineFeatures.push(feature);

                                } else if (pushpin.end_lat && pushpin.end_lng) {
                                    const coords = [[pushpin.lng, pushpin.lat], [pushpin.end_lng, pushpin.end_lat]];
                                    const geometry = {
                                        type: 'LineString',
                                        coordinates: coords
                                    };
                                    const feature = {
                                        type: 'Feature',
                                        geometry: geometry,
                                        properties: properties
                                    };
                                    lineFeatures.push(feature);
                                }
                            } else {
                                const geometry = {
                                    type: 'Point',
                                    coordinates: [pushpin.lng, pushpin.lat]
                                };
                                const feature = {
                                    type: 'Feature',
                                    geometry: geometry,
                                    properties: properties
                                };
                                pointFeatures.push(feature);
                            }
                        });
                    }
                });

                const features = pointFeatures.concat(lineFeatures);
                const geojson: any = {
                    type: 'FeatureCollection',
                    features: features
                };

                // Generate ShapeFile
                console.log(geojson);
                const options = {
                    outputType: "blob",
                    compression: "DEFLATE"
                } as any;
                //We gebruiken een locale package van shp-write om deze rede: https://github.com/mapbox/shp-write/pull/129/commits/3c255aebb05b95913c1f0b3f4bffb337e20e0981
                shpwrite.zip(geojson, options).then(buffer => {
                    console.log(buffer);
                    const filename = `VVR_${this.inspection?.name || this.customerArea.name}_${formatDate(new Date(), 'yyyy-MMM-d_H-mm-ss', 'nl')}.zip`;
                    saveAs(new Blob([buffer as BlobPart]), filename.replace('.', ''));
                    this.dialogRef.close();
                    this.exporting = false;
                });
            }));
        });
    }

    private getAllData(pushpinPart: PushpinPart, keyValMap: {}) {
        pushpinPart.decoration = keyValMap['decoration_id'][pushpinPart.decoration_id];
        pushpinPart.material = keyValMap['material_id'][pushpinPart.material_id];
        pushpinPart.paint_color = keyValMap['paint_color_id'][pushpinPart.paint_color_id];
        pushpinPart.status = keyValMap['status_id'][pushpinPart.status_id];
    }

}
